// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import { N20, N30A, N700, P300, P400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { NowLocation } from '../../../../utils/constants/schedule';
import {
	LINE_WIDTH,
	ROTATION_FIRST_MARGIN,
	ROTATION_HEIGHT,
	ROTATION_MARGIN,
	SIDE_HEADER_WIDTH,
	TIME_HEADER_HEIGHT,
	TimeDisplay,
} from '../constants';
import { type TimelineVariant } from '../types';

const STRIPE_WIDTH = 8 * 0.25;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Container = styled.div({
	position: 'relative',
	height: '100%',
	width: '100%',
	boxSizing: 'inherit',
	backgroundColor: token('color.background.neutral', N20),
});

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Line = styled.div<{
	index: number;
	ratio: number;
	variant: TimelineVariant;
}>`
	position: absolute;
	height: 100%;
	border-left: ${LINE_WIDTH}px dashed ${token('color.border', N30A)};
	z-index: 1;

	${({ ratio, variant, index }) => {
		switch (variant) {
			case 'compact':
				return `
          left: calc(${ratio}% * ${index});

          &:first-of-type {
            display: none;
          }
        `;
			case 'full':
				return `
          left: calc(((100% - ${SIDE_HEADER_WIDTH}px) * ${ratio} * ${index}) / 100 + ${SIDE_HEADER_WIDTH}px);
        `;
		}
	}}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const EmptyTime = styled.div({
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${SIDE_HEADER_WIDTH}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${SIDE_HEADER_WIDTH}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Inner = styled.div({
	display: 'flex',
	flexDirection: 'row',
	width: '100%',
	height: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Header = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${TIME_HEADER_HEIGHT}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: `${TIME_HEADER_HEIGHT}px`,
	width: '100%',
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Cell = styled.div<{
	ratio: number;
	display: TimeDisplay;
	nowLocation: NowLocation;
	variant: TimelineVariant;
}>`
	display: flex;
	justify-content: center;
	flex-direction: column;
	width: ${({ ratio }) => ratio}%;

	${({ nowLocation }) => {
		switch (nowLocation) {
			case NowLocation.InRange:
				return `
          color: ${token('color.text.accent.purple', P300)};
        `;
			default:
				return `
          color: ${token('color.text.subtlest', N700)};
        `;
		}
	}}

	${({ display }) => {
		switch (display) {
			case TimeDisplay.HorizontalDay:
			case TimeDisplay.VerticalDay:
				return `
          align-items: center;
        `;
			case TimeDisplay.Hour:
				return `
          align-items: flex-start;
        `;
		}
	}}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Time = styled.p<{ display: TimeDisplay }>`
	line-height: 16px;

	${({ display }) => {
		switch (display) {
			case TimeDisplay.Hour:
				return `
          margin: 0 0 0 ${token('space.100', '8px')};
        `;
			default:
				return `
          margin: 0;
        `;
		}
	}}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Stripe = styled.div<{
	startEpoch: number;
	nowEpoch: number;
	endEpoch: number;
	variant: TimelineVariant;
}>`
	background-color: ${token('color.text.accent.purple', P400)};
	height: 100%;
	position: absolute;
	top: 0;
	width: ${STRIPE_WIDTH}px;
	z-index: 3;

	${({ nowEpoch, endEpoch, startEpoch, variant }) => {
		const ratio = (nowEpoch - startEpoch) / (endEpoch - startEpoch);
		const halfWidth = STRIPE_WIDTH / 2;

		if (variant === 'compact') {
			return `left: calc((100%  * ${ratio}) - ${halfWidth}px);`;
		}

		if (variant === 'full') {
			// appropriate ratio of whole for smaller part (exclude rotation name width)
			return `left: calc(((100% - ${SIDE_HEADER_WIDTH}px) * ${ratio}) + ${SIDE_HEADER_WIDTH}px);`;
		}
	}};
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
export const List = styled.div<{ size: 'small' | 'medium' | 'large' }>((props) => ({
	minHeight: `${
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		ROTATION_FIRST_MARGIN + ROTATION_HEIGHT[props.size] + ROTATION_MARGIN[props.size]
	}px`,
}));
